import type {JsonResponseEnvelope} from '@PosterWhiteboard/collaboration/poster-live-edits.types';
import {
  CSRF_COOKIE_NAME,
  CSRF_TOKEN_NAME_META,
  CSRF_TOKEN_SOURCE_COOKIE,
  CSRF_TOKEN_SOURCE_FIELD,
  DEFAULT_CSRF_TOKEN_NAME,
  KEEPALIVE_MAX_BODY_BYTES,
} from '@PosterWhiteboard/collaboration/poster-live-edits.types';

const SUCCESS_STATUS = 'success';

/** An endpoint's refusal, shaped like the error the ordinary AJAX helper throws so the same handling applies. */
class KeepaliveRequestError extends Error {
  public constructor(
    message: string,
    public readonly code: string | null
  ) {
    super(message);
  }
}

/** Whether the fields fit a keepalive request and the CSRF token the server needs is at hand. */
export const canPublishWithKeepalive = (fields: Record<string, string>): boolean => {
  const bodyBytes = Object.values(fields).reduce((bytes, value) => bytes + value.length, 0);
  return bodyBytes <= KEEPALIVE_MAX_BODY_BYTES && readCookie(CSRF_COOKIE_NAME) !== null;
};

/**
 * Posts the fields the way the ordinary AJAX helper does, as a request the browser completes even
 * after the page has closed. Resolves with the endpoint's data, or throws its refusal with the code.
 */
export const publishWithKeepalive = async (endpoint: string, fields: Record<string, string>): Promise<unknown> => {
  const body = new FormData();
  body.append(getCsrfTokenName(), readCookie(CSRF_COOKIE_NAME) ?? '');
  body.append(CSRF_TOKEN_SOURCE_FIELD, CSRF_TOKEN_SOURCE_COOKIE);
  for (const [name, value] of Object.entries(fields)) {
    body.append(name, value);
  }
  const response = await fetch(window.PMW.util.site_url(endpoint), {method: 'POST', body, keepalive: true, credentials: 'same-origin'});
  const envelope = (await response.json()) as JsonResponseEnvelope;
  if (envelope.status !== SUCCESS_STATUS) {
    throw new KeepaliveRequestError(envelope.message ?? '', envelope.code ?? null);
  }
  return envelope.data;
};

const getCsrfTokenName = (): string => {
  const metaTag = document.querySelector(`meta[name="${CSRF_TOKEN_NAME_META}"]`);
  return metaTag instanceof HTMLMetaElement && metaTag.content !== '' ? metaTag.content : DEFAULT_CSRF_TOKEN_NAME;
};

const readCookie = (name: string): string | null => {
  const prefix = `${name}=`;
  const cookie = document.cookie.split('; ').find((entry) => entry.startsWith(prefix));
  return cookie === undefined ? null : decodeURIComponent(cookie.slice(prefix.length));
};
